import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import './index.css';
import App from './App';
import {StrictMode} from 'react';
import ErrorBoundary from './ErrorBoundary';
import authStorage from './utils/authStorage';
import { HelmetProvider } from 'react-helmet-async';

// Auto-attach JWT token to all axios requests
axios.interceptors.request.use((config) => {
    const token = authStorage.getItem('accessToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auto-refresh JWT token on 401 errors
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If error is 401, not a retry, and not hitting the refresh or login endpoint
        if (
            error.response?.status === 401 && 
            !originalRequest._retry && 
            !originalRequest.url.includes('/accounts/token/refresh/') &&
            !originalRequest.url.includes('/accounts/login/')
        ) {
            originalRequest._retry = true;
            const refreshToken = authStorage.getItem('refreshToken');
            
            if (refreshToken) {
                try {
                    // Call token refresh endpoint
                    const res = await axios.post('/accounts/token/refresh/', { refresh: refreshToken });
                    
                    if (res.status === 200) {
                        // Store the new tokens
                        const isRemembered = authStorage.isRemembered();
                        authStorage.setItem('accessToken', res.data.access, isRemembered);
                        if (res.data.refresh) {
                            authStorage.setItem('refreshToken', res.data.refresh, isRemembered);
                        }
                        
                        // Update the authorization header and retry original request
                        originalRequest.headers.Authorization = `Bearer ${res.data.access}`;
                        return axios(originalRequest);
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    // Clear storage and redirect to login if refresh fails
                    authStorage.clear();
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
            } else {
                // No refresh token available, clear storage and redirect
                authStorage.clear();
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <StrictMode>
        <HelmetProvider>
            <ErrorBoundary>
                <App/>
            </ErrorBoundary>
        </HelmetProvider>
    </StrictMode>
);
